/**
 * 商品ページの「送料の目安」（snippets/bpjp-shipping-estimate.liquid）を埋める。
 *
 * 設計:
 *   - 帯は配送プロファイルと同じ「下限g 以上・上限g 以下」で選ぶ（整数除算で番号を出さない＝境界でずれない）。
 *   - 料金は JPY。閲覧者の通貨が JPY 以外なら Shopify.currency.rate で換算し、そのマーケットの丸め
 *     （料金表の r＝丸めあり の国だけ・通貨ごとの切り上げ）をかけてチェックアウトと同じ額にする。
 *     丸め規則が分からない通貨だけ "about" を付ける（2026-10-15 KR で ₩15,694 と出してチェックアウト ₩16,000 とずれた是正）。
 *   - variant を切り替えたら Dawn の pubsub（PUB_SUB_EVENTS.variantChange）で出し直す。
 *   - 当てはまる帯が無い・重量 0・データが壊れている → 何も出さない（hidden のまま）。
 */
(function () {
  'use strict';

  var METHOD_LABELS = { 'Surface mail': 'surface mail', 'Standard air mail': 'air mail', 'International Air Packet (tracked)': 'tracked air packet' };

  function readJson(el, selector) {
    var node = el.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /** その重量に当たる帯の料金（JPY）。無ければ null。 */
  function priceFor(bands, grams) {
    for (var i = 0; i < bands.length; i++) {
      if (grams >= bands[i][0] && grams <= bands[i][1]) return bands[i][2];
    }
    return null;
  }

  /**
   * この国の料金表で「まとめると安い」が言えるか（2点まとめた最安 ≤ 別々の最安の和が、100g 刻みの全組み合わせで成り立つか）。
   * ★2kg までのエアパケットがあり船便が無い国（第3地帯の航空便のみ＝BE・NZ・MX）では、合計 2kg 超で崩れる
   *   （2026-10-17 全組み合わせで確認・台帳 small-packet-shipping-a4-1-20261017）。料金表から毎回判定するので国名を持たない。
   */
  function combineSaves(rates) {
    function cheapest(g) {
      var best = null;
      for (var i = 0; i < rates.length; i++) {
        var p = priceFor(rates[i].b || [], g);
        if (p !== null && (best === null || p < best)) best = p;
      }
      return best;
    }
    var STEP = 100, MAX = 30000, memo = {};
    function c(g) { if (!(g in memo)) memo[g] = cheapest(g); return memo[g]; }
    for (var a = STEP; a <= MAX; a += STEP) {
      if (c(a) === null) continue;
      for (var b = a; a + b <= MAX; b += STEP) {
        if (c(b) === null || c(a + b) === null) continue;
        if (c(a + b) > c(a) + c(b)) return false;
      }
    }
    return true;
  }

  /**
   * マーケットの価格の丸め（チェックアウトは送料にもかける）。通貨ごとに「その単位の倍数へ切り上げ」。
   * 公式ヘルプに通貨別の表が無いため、2026-10-15 に本番の商品価格（27か国×19〜22商品）から実測した値。
   * EUR だけは「.95 へ切り上げ」（€65.58→€65.95・€553.95→€553.95）。表に無い通貨は丸めない（"about" のまま）。
   */
  var ROUND_STEP = {
    USD: 1, CAD: 1, AUD: 1, NZD: 1, SGD: 1, MYR: 1, THB: 1, TWD: 1, PEN: 1,
    CHF: 1, CZK: 1, DKK: 1, GBP: 1, PLN: 1, RON: 1, SEK: 1,
    INR: 100, PHP: 100, HUF: 100, ISK: 100,
    KRW: 1000, IDR: 1000, VND: 1000
  };

  /** 丸めた金額と、チェックアウトと一致する見込みか（丸め規則が分かっている）を返す。 */
  function roundForMarket(amount, currency, rounding) {
    // rounding: 1＝丸めあり／0＝丸めなし／null・未定義＝不明（料金表に丸めの有無が無い国）→ 確定額にせず "about"
    if (rounding !== 1 && rounding !== 0) return { value: amount, exact: false };
    if (rounding === 0) return { value: amount, exact: true };
    // 浮動小数の誤差で 553.95 が 553.9500000001 になって1段上がらないよう、小さく丸めてから切り上げる
    var a = Math.round(amount * 1e6) / 1e6;
    if (currency === 'EUR') return { value: Math.ceil(a + 0.05) - 0.05, exact: true };
    var step = ROUND_STEP[currency];
    if (!step) return { value: amount, exact: false };
    return { value: Math.ceil(a / step) * step, exact: true };
  }

  function formatMoney(jpy, rounding) {
    var cur = window.Shopify && window.Shopify.currency;
    var active = cur && cur.active;
    var rate = cur && Number(cur.rate);
    if (!active || active === 'JPY' || !(rate > 0)) {
      return '¥' + Math.round(jpy).toLocaleString('en-US');
    }
    try {
      var r = roundForMarket(jpy * rate, active, rounding);
      var text = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: active }).format(r.value);
      return r.exact ? text : 'about ' + text;
    } catch (e) {
      return '¥' + Math.round(jpy).toLocaleString('en-US');
    }
  }

  function render(el, variantId) {
    var data = readJson(el, '[data-bpjp-ship-rates]'); // {r: 丸め(1/0), m: [{m: メソッド名, b: [[下限g, 上限g, 料金JPY], ...]}]}
    var rates = data && data.m;
    var rounding = data ? data.r : null;
    var weights = readJson(el, '[data-bpjp-ship-weights]');
    var textEl = el.querySelector('[data-bpjp-ship-text]');
    var grams = weights && Number(weights[String(variantId)]);
    if (!rates || !rates.length || !textEl || !(grams > 0)) {
      el.hidden = true;
      return;
    }
    var options = [];
    rates.forEach(function (method) {
      var jpy = priceFor(method.b || [], grams);
      if (jpy !== null) options.push({ label: METHOD_LABELS[method.m] || String(method.m).toLowerCase(), jpy: jpy });
    });
    if (!options.length) {
      el.hidden = true;
      return;
    }
    options.sort(function (a, b) { return a.jpy - b.jpy; });
    var parts = options.map(function (o) { return formatMoney(o.jpy, rounding) + ' by ' + o.label; });
    var strong = document.createElement('strong');
    strong.textContent = 'Shipping to ' + (el.dataset.countryName || 'your country') + ' for this item alone: ';
    textEl.textContent = '';
    textEl.appendChild(strong);
    textEl.appendChild(document.createTextNode(parts.join(' · ') + '. '));
    var note = document.createElement('span');
    note.className = 'bpjp-ship-est__note';
    // 「まとめると安い」は最初の帯の料金 ≥ 1kg ごとの増分である限り全ゾーンで成り立つ（2026-10-17 全組み合わせで確認・
    // 台帳 cart-additional-item-shipping-a1-20261017）。配送料金を改定したら、この文がまだ言えるかを確かめ直す。
    // ただし食玩は別の配送プロファイル（Shokugan (No US shipping)）で、他の商品と混ぜると送料はプロファイルごとに合算される
    // （2026-10-17 本番カートで実測・台帳 a1-note-mixed-profile-fix-20261017）＝例外として明記する。
    // まとめても安くならない組み合わせがある国では「combine items to save」を言わない（RYO 2026-10-17 案A）。
    note.textContent = combineSaves(rates)
      ? 'Shipping is calculated by total weight — combine items to save (candy toys ship in a separate parcel). Exact cost at checkout.'
      : 'Shipping is calculated by total weight (candy toys ship in a separate parcel). Exact cost at checkout.';
    textEl.appendChild(note);
    el.dataset.variantId = String(variantId);
    el.hidden = false;
  }

  function initAll() {
    document.querySelectorAll('[data-bpjp-ship-est]').forEach(function (el) {
      if (el.dataset.bpjpShipInit) return;
      el.dataset.bpjpShipInit = '1';
      render(el, el.dataset.variantId);
    });
  }

  function onVariantChange(event) {
    var data = event && event.data;
    if (!data) return;
    initAll(); // セクションが差し替わった場合に備えて未初期化のものを先に埋める
    document.querySelectorAll('[data-bpjp-ship-est]').forEach(function (el) {
      if (el.dataset.sectionId !== String(data.sectionId)) return;
      // 組み合わせが存在しない（variant 未確定）ときは、直前の variant の送料を残さず隠す（Codex 指摘）
      if (!data.variant) {
        el.hidden = true;
        return;
      }
      render(el, data.variant.id);
    });
  }

  function start() {
    initAll();
    // constants.js の PUB_SUB_EVENTS は const（window のプロパティにならない）なので typeof で見る。
    // どちらも <head> の defer で読まれ、この defer スクリプトより先に実行される。
    /* global subscribe, PUB_SUB_EVENTS */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
